import React, { useEffect, useMemo, useState } from 'react';
import EngagerSliderRow from './calc_components/EngagerSliderRow';
import CalculatorToolsRail from '../components/CalculatorToolsRail';
import { inrAmountInWordsShort } from './calc_components/calculatorAmountWords';
import CalculatorWhatThisMeans from './calc_components/CalculatorWhatThisMeans';
import CalculatorPrecisionToggle from './calc_components/CalculatorPrecisionToggle';
import { useCalcAmountDisplayMode } from './calc_components/useCalcAmountDisplayMode';
import { formatYearsCount } from './calc_components/calculatorFormatters';
import { computeEmi } from './EmiCalculator';
import './calculators.css';
import './EmiInputBands.css';
import './TenureInterestCalculator.css';

const formatterINR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const minInterestRate = 4;
const maxInterestRate = 20;
const interestRateStep = 0.25;

const minTenureYears = 1;
const maxTenureYears = 30;

const minLoanAmount = 0;
const maxLoanAmount = 1000000000;

const STANDALONE_DEFAULTS = {
  interestRate: 9,
  tenureYears: 20,
  loanAmount: 10000000,
};

function formatInterestRatePct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)}%`;
}

function snapInterestRate(n) {
  const raw = Number(n);
  if (!Number.isFinite(raw)) return STANDALONE_DEFAULTS.interestRate;
  const snapped =
    minInterestRate +
    Math.round((raw - minInterestRate) / interestRateStep) * interestRateStep;
  return Math.min(
    maxInterestRate,
    Math.max(minInterestRate, Number(snapped.toFixed(2))),
  );
}

/** Total interest over full tenure for a fixed-rate amortising loan. */
export function computeTotalInterest(loanAmount, annualInterestRatePct, tenureYears) {
  const P = Number(loanAmount);
  const years = Number(tenureYears);
  if (!Number.isFinite(P) || P <= 0 || !Number.isFinite(years) || years <= 0) {
    return 0;
  }
  const n = Math.round(years * 12);
  const emi = computeEmi(P, annualInterestRatePct, years);
  return Math.max(0, emi * n - P);
}

export default function TenureInterestCalculator({ embed = false }) {
  useEffect(() => {
    if (!embed) window.scrollTo(0, 0);
  }, [embed]);

  const [interestRate, setInterestRate] = useState(STANDALONE_DEFAULTS.interestRate);
  const [tenureYears, setTenureYears] = useState(STANDALONE_DEFAULTS.tenureYears);
  const [loanAmount, setLoanAmount] = useState(STANDALONE_DEFAULTS.loanAmount);
  const [loanAmountForWords, setLoanAmountForWords] = useState(
    STANDALONE_DEFAULTS.loanAmount,
  );

  const totalInterest = useMemo(
    () => computeTotalInterest(loanAmount, interestRate, tenureYears),
    [loanAmount, interestRate, tenureYears],
  );
  const monthlyEmi = useMemo(
    () => computeEmi(loanAmount, interestRate, tenureYears),
    [loanAmount, interestRate, tenureYears],
  );

  const loanAmountWords =
    loanAmountForWords > 0
      ? inrAmountInWordsShort(Math.round(loanAmountForWords))
      : '';

  function handleStartOver(e) {
    e.preventDefault();
    setInterestRate(STANDALONE_DEFAULTS.interestRate);
    setTenureYears(STANDALONE_DEFAULTS.tenureYears);
    setLoanAmount(STANDALONE_DEFAULTS.loanAmount);
    setLoanAmountForWords(STANDALONE_DEFAULTS.loanAmount);
  }

  const calculatorCard = (
    <div
      className={`appContainer nps-ups--compact emi-calculator tenure-interest-calculator${
        embed ? ' nps-ups--embed' : ''
      }`}
    >
      <h1 className="tool-heading">Tenure Interest Calculator</h1>

      {!embed ? (
        <div className="col-light-grey italic font-size-small nps-ups-intro">
          <p>
            See how stretching or shortening your loan tenure changes the total interest you pay
            over the full term.
          </p>
        </div>
      ) : null}

      <TenureInterestResultPanel
        embed={embed}
        totalInterest={totalInterest}
        monthlyEmi={monthlyEmi}
        tenureYears={tenureYears}
        loanAmount={loanAmount}
      />

      <div className="border-top-light nps-ups-divider" />

      {/* In article embeds, tenure sits first so it stays next to the bars. */}
      {embed ? (
        <section className="emi-input-band emi-input-band--b">
          <EngagerSliderRow
            id="ti-tenure-years"
            label="Tenure in years"
            tooltip="Total repayment period for the loan, in whole years. Longer tenure usually means lower EMI but higher total interest."
            value={tenureYears}
            setValue={setTenureYears}
            min={minTenureYears}
            max={maxTenureYears}
            step={1}
            format={formatYearsCount}
            variant="inr"
            defaultValue={STANDALONE_DEFAULTS.tenureYears}
          />
        </section>
      ) : null}

      <section className="emi-input-band emi-input-band--b">
        <EngagerSliderRow
          id="ti-interest-rate"
          label="Interest rate"
          tooltip="Annual interest rate on the loan. Illustrative only—actual rates vary by lender and product."
          value={interestRate}
          setValue={(v) => setInterestRate(snapInterestRate(v))}
          min={minInterestRate}
          max={maxInterestRate}
          step={interestRateStep}
          format={formatInterestRatePct}
          variant="inr"
          defaultValue={STANDALONE_DEFAULTS.interestRate}
        />
      </section>

      <section className="emi-input-band emi-input-band--b">
        <div className="savings-goal-first-year-exp-field">
          <EngagerSliderRow
            id="ti-loan-amount"
            label="Loan amount"
            tooltip="Principal borrowed, before interest and fees."
            value={Math.min(loanAmount, maxLoanAmount)}
            setValue={(v) => {
              const next = Math.min(v, maxLoanAmount);
              setLoanAmount(next);
              setLoanAmountForWords(next);
            }}
            onLiveValueChange={(n) => {
              if (n != null && Number.isFinite(n)) {
                setLoanAmountForWords(Math.min(maxLoanAmount, Math.max(0, n)));
              } else {
                setLoanAmountForWords(0);
              }
            }}
            min={minLoanAmount}
            max={maxLoanAmount}
            step={100000}
            format={(v) => formatterINR.format(v)}
            variant="inr"
            defaultValue={STANDALONE_DEFAULTS.loanAmount}
          />
          {!embed && loanAmountWords ? (
            <p className="calc-output-amount-in-words savings-goal-first-year-exp-words">
              {loanAmountWords}
            </p>
          ) : null}
        </div>
      </section>

      {!embed ? (
        <section className="emi-input-band emi-input-band--b">
          <EngagerSliderRow
            id="ti-tenure-years"
            label="Tenure in years"
            tooltip="Total repayment period for the loan, in whole years. Longer tenure usually means lower EMI but higher total interest."
            value={tenureYears}
            setValue={setTenureYears}
            min={minTenureYears}
            max={maxTenureYears}
            step={1}
            format={formatYearsCount}
            variant="inr"
            defaultValue={STANDALONE_DEFAULTS.tenureYears}
          />
        </section>
      ) : null}

      {!embed ? (
        <>
          <button type="button" className="calc-start-over-btn" onClick={handleStartOver}>
            Start over
          </button>

          <p className="font-small font-light-grey italic margin-0 nps-ups-footnote">
            Illustrative total interest from the standard amortisation formula; excludes processing
            fees, insurance, prepayment, and tax effects. Not lender or product advice.
          </p>
        </>
      ) : null}
    </div>
  );

  if (embed) return calculatorCard;

  return (
    <div className="calculator-page-layout">
      {calculatorCard}
      <div className="calculator-rail-shell">
        <CalculatorToolsRail currentToolKey="tenure-interest-calculator" />
      </div>
    </div>
  );
}

function TenureInterestBars({ loanAmount, totalInterest, compact = false }) {
  const { formatInr } = useCalcAmountDisplayMode();
  const principal = Number(loanAmount) > 0 ? Number(loanAmount) : 0;
  const interest = Number(totalInterest) > 0 ? Number(totalInterest) : 0;
  const scaleMax = Math.max(principal, interest, 1);
  const principalPct = Math.min(100, (principal / scaleMax) * 100);
  const interestPct = Math.min(100, (interest / scaleMax) * 100);
  const principalDisplay = formatInr(principal);
  const interestDisplay = formatInr(interest);

  return (
    <div
      className={`tenure-interest-bars${compact ? ' tenure-interest-bars--compact' : ''}`}
      role="img"
      aria-label={`Principal ${principalDisplay} versus total interest ${interestDisplay}`}
    >
      <div className="tenure-interest-bars__row">
        <div className="tenure-interest-bars__bar-head" aria-hidden="true">
          <div
            className="tenure-interest-bars__bar-meta"
            style={{ width: `${principalPct}%` }}
          >
            <p className="tenure-interest-bars__label tenure-interest-bars__label--principal">
              Principal
            </p>
            <p className="tenure-interest-bars__value">{principalDisplay}</p>
          </div>
        </div>
        <div className="tenure-interest-bars__track">
          <div
            className="tenure-interest-bars__fill tenure-interest-bars__fill--principal"
            style={{ width: `${principalPct}%` }}
          />
        </div>
      </div>

      <div className="tenure-interest-bars__row">
        <div className="tenure-interest-bars__bar-head" aria-hidden="true">
          <div
            className="tenure-interest-bars__bar-meta"
            style={{ width: `${interestPct}%` }}
          >
            <p className="tenure-interest-bars__label tenure-interest-bars__label--interest">
              Total Interest
            </p>
            <p className="tenure-interest-bars__value">{interestDisplay}</p>
          </div>
        </div>
        <div className="tenure-interest-bars__track">
          <div
            className="tenure-interest-bars__fill tenure-interest-bars__fill--interest"
            style={{ width: `${interestPct}%` }}
          />
        </div>
      </div>

      {!compact ? (
        <p className="tenure-interest-bars__caption">
          Bars share the same scale so you can compare interest against the loan principal.
        </p>
      ) : null}
    </div>
  );
}

function TenureInterestResultPanel({
  embed = false,
  totalInterest,
  monthlyEmi,
  tenureYears,
  loanAmount,
}) {
  const [showResult, setShowResult] = useState(true);
  const { formatInr, resolveAmount } = useCalcAmountDisplayMode();
  const totalInterestDisplay = formatInr(totalInterest);
  const wordsLine =
    !embed && Number.isFinite(totalInterest) && totalInterest > 0
      ? inrAmountInWordsShort(resolveAmount(totalInterest))
      : '';
  const emiDisplay = formatInr(monthlyEmi);
  const principalSharePct =
    Number.isFinite(loanAmount) &&
    loanAmount > 0 &&
    Number.isFinite(totalInterest) &&
    totalInterest >= 0
      ? Math.round((totalInterest / (loanAmount + totalInterest)) * 100)
      : null;

  return (
    <div className="grid-center-display-result nps-ups-result-panel nps-ups-result-outer calc-result-legacy nps-result-legacy">
      <CalculatorPrecisionToggle />
      <button
        type="button"
        className="btn-small-round nps-ups-result-help"
        onClick={() => setShowResult((s) => !s)}
        aria-label={showResult ? 'Show explanation' : 'Show number'}
      >
        {showResult ? '?' : '×'}
      </button>

      {showResult ? (
        <>
          <h4>Principal vs interest over {formatYearsCount(tenureYears)}</h4>
          <TenureInterestBars
            loanAmount={loanAmount}
            totalInterest={totalInterest}
            compact={embed}
          />
          {wordsLine ? (
            <p className="calc-output-amount-in-words">{wordsLine} interest</p>
          ) : null}
        </>
      ) : (
        <>
          <h4 className="calc-wtm-heading">What this means</h4>
          <CalculatorWhatThisMeans
            regionLabel="Explanation of your total interest result"
            heroValue={totalInterestDisplay}
            heroVariant="inr"
            tagline={
              <p>
                <strong>Interest cost of the full tenure</strong>—move the tenure slider to see
                how longer loans raise total interest even when monthly EMI falls.
              </p>
            }
            highlight={
              <>
                Monthly EMI would be about <em>{emiDisplay}</em>
                {principalSharePct != null ? (
                  <>
                    ; interest is roughly <em>{principalSharePct}%</em> of everything you repay.
                  </>
                ) : (
                  '.'
                )}
              </>
            }
            highlightTone="warning"
            foot="Illustrative only—not advice."
          />
        </>
      )}
    </div>
  );
}
